import * as m from '@staiter/i18n';
import type { SettlementLine } from '@staiter/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo } from 'react';
import { Tier3SendAction } from '~/components/approvals';
import { Breadcrumbs, PageHeader, Table, type TableColumn } from '~/components/data';
import { PeriodCloseBanner } from '~/components/domain';
import { Button } from '~/components/primitives';
import { cancelSettlement, fetchSettlement, postSettlement } from '~/lib/api/settlements';
import { formatDate, formatMoney } from '~/lib/format';
import shared from './_auth._shared.module.css';

/**
 * S14-FE.9 — Settlement detail page.
 */

export const Route = createFileRoute('/_auth/settlements/$id/')({ component: SettlementDetail });

function SettlementDetail(): JSX.Element {
  const { id } = Route.useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const query = useQuery({ queryKey: ['settlements', id], queryFn: () => fetchSettlement(id) });

  const postMut = useMutation({
    mutationFn: () => postSettlement(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['settlements'] }),
  });
  const cancelMut = useMutation({
    mutationFn: () => cancelSettlement(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['settlements'] }),
  });

  // FE Pass 29: header/value localized (was hardcoded 'Direction' + raw enum).
  const lineColumns: TableColumn<SettlementLine>[] = useMemo(
    () => [
      {
        id: 'no',
        header: m.settlement_column_line_no(),
        accessor: (r) => String(r.line_no),
        width: '60px',
        mono: true,
      },
      {
        id: 'direction',
        header: m.settlement_column_direction(),
        accessor: (r) =>
          r.direction === 'ar_to_ap'
            ? m.settlement_direction_ar_to_ap()
            : m.settlement_direction_ap_to_ar(),
        width: '140px',
      },
      {
        id: 'invoice',
        header: m.invoice_column_doc_number(),
        // FE-84 honest-audit: prefer voucher_no denorm (BE-10 v0.3.215).
        accessor: (r) => {
          const vn = (r as { invoice_voucher_no?: string | null }).invoice_voucher_no;
          if (vn) {
            return (
              <a
                href={`/invoices/${r.invoice_id}`}
                style={{ color: 'var(--color-accent)', textDecoration: 'none' }}
              >
                {vn}
              </a>
            );
          }
          return r.invoice_id.slice(0, 8);
        },
        mono: true,
      },
      {
        id: 'amount',
        header: m.payment_column_amount(),
        accessor: (r) => formatMoney(r.amount),
        mono: true,
        align: 'right',
      },
    ],
    [],
  );
  const stateLabel = (st: string): string => {
    if (st === 'draft') return m.settlement_state_draft();
    if (st === 'posted') return m.settlement_state_posted();
    if (st === 'cancelled') return m.settlement_state_cancelled();
    return st;
  };

  const s = query.data;
  // FE-282 Pass 54: defensive fallback при query error (Zod parse failure из-за
  // BE-N — settlement.lines.min(2) violated by single-line seed data). Раньше
  // показывали raw «Not found», теперь — informative error UI.
  if (!s) {
    return (
      <div>
        <PageHeader title={m.phase2_settlements_title()} />
        {query.isLoading ? (
          <div>…</div>
        ) : query.isError ? (
          <div
            style={{
              padding: 'var(--space-4)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
              background: 'var(--color-bg-elevated)',
              maxWidth: 500,
              marginTop: 'var(--space-3)',
            }}
          >
            <strong>{m.ui_dokument_nedostupen()}</strong>
            <p style={{ color: 'var(--color-text-secondary)', marginTop: 8 }}>
              {m.ui_server_vernul_dannye_kotorye_my_ne_mozhem_otobrazit_obrat()}
            </p>
          </div>
        ) : (
          <div>{m.ui_dokument_ne_nayden()}</div>
        )}
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs
            items={[
              { label: m.phase2_settlements_title(), to: '/settlements' },
              { label: s.document_number },
            ]}
          />
        }
        title={`${m.phase2_settlements_title()} · ${s.document_number}`}
        actions={
          <>
            {s.state === 'draft' && (
              <Button
                variant="secondary"
                onClick={() => nav({ to: `/settlements/${s.id}/edit` as never })}
              >
                {m.common_button_edit()}
              </Button>
            )}
            {s.state === 'draft' && (
              <Button onClick={() => postMut.mutate()} loading={postMut.isPending}>
                {m.invoice_button_post()}
              </Button>
            )}
            {s.state === 'posted' && (
              <Button
                variant="ghost"
                onClick={() => cancelMut.mutate()}
                loading={cancelMut.isPending}
              >
                {m.invoice_button_cancel()}
              </Button>
            )}
            <Tier3SendAction
              label={m.invoice_button_push_1c()}
              subject={{
                taskType: 'documents_push_1c',
                subjectKind: 'settlement',
                payload: { id },
              }}
            />
          </>
        }
      />
      <PeriodCloseBanner scope="settlement" />
      <div className={shared.statGrid4}>
        {/* access-control (F9) — компания записи. */}
        <div>
          <div className={shared.statLabel}>{m.cp_field_company()}</div>
          <div>{(s as { entity_name?: string | null }).entity_name ?? '—'}</div>
        </div>
        <div>
          <div className={shared.statLabel}>{m.settlement_column_date()}</div>
          <div>{formatDate(s.settlement_date)}</div>
        </div>
        <div>
          <div className={shared.statLabel}>{m.settlement_column_net_amount()}</div>
          <div data-testid="settlement-net">{formatMoney(s.net_amount)}</div>
        </div>
        <div>
          <div className={shared.statLabel}>{m.settlement_column_state()}</div>
          <div>
            {/* FE-89: StatusBadge pill — bg/color по state. */}
            <span
              style={{
                display: 'inline-block',
                padding: '2px 10px',
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 500,
                background:
                  s.state === 'posted'
                    ? 'var(--ok-bg-15)'
                    : s.state === 'cancelled'
                      ? 'var(--danger-bg-15)'
                      : 'var(--warn-bg-15)',
                color:
                  s.state === 'posted'
                    ? 'var(--color-ok)'
                    : s.state === 'cancelled'
                      ? 'var(--color-danger)'
                      : 'var(--color-warn)',
              }}
            >
              {stateLabel(s.state)}
            </span>
          </div>
        </div>
      </div>
      <Table<SettlementLine>
        data={s.lines}
        columns={lineColumns}
        rowKey={(r) => String(r.line_no)}
        ariaLabel="settlement lines"
        emptyTitle={m.settlement_empty_title()}
      />
    </div>
  );
}
